import React, { type ReactNode, useRef, useEffect } from "react";
import {
  Box,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Flex,
  useColorModeValue,
  Portal,
  List,
} from "@chakra-ui/react";
import { BiSearch } from "react-icons/bi";
import { AiOutlineCloseCircle } from "react-icons/ai";
import Spinner from "@components/common/Spinner";
import { theme } from "src/styles/theme";

interface IInputSelectSearchAccount {
  title: string;
  value: string;
  onChangeInput: React.ChangeEventHandler<HTMLInputElement> | undefined;
  placeholder?: string;
  isSelected?: string;
  height?: string;
  width?: string;
  disabled?: boolean;
  disabledButton?: boolean;
  isRequirement?: boolean;
  onChangeIcon: React.MouseEventHandler<SVGElement>;
  onClickSearchIcon: () => void;
  loading?: boolean;
  children?: ReactNode;
  isOportunity?: boolean;
  maxLength?: number;
}

const InputSelectSearchAccount = ({
  title,
  value,
  onChangeInput,
  placeholder,
  isSelected,
  height,
  width,
  disabled,
  disabledButton,
  isRequirement,
  onChangeIcon,
  onClickSearchIcon,
  loading,
  children,
  isOportunity,
  maxLength,
}: IInputSelectSearchAccount) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Cores
  const primaryColor = useColorModeValue(theme.primary, theme.primary);
  const textColor = useColorModeValue(theme.text, theme.text);
  const disabledColor = useColorModeValue(theme.disable, theme.disable);
  const borderColor = useColorModeValue(theme.disable, theme.disable);

  // Função para calcular a posição do dropdown considerando modais
  const calculateDropdownPosition = React.useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();

    // Verifica se está dentro de um modal
    const modalElement = wrapperRef.current.closest(
      '[role="dialog"], .chakra-modal__content, .modal, [data-testid="modal"]'
    );

    let calculatedLeft = rect.left;
    let calculatedTop = rect.bottom + window.scrollY;
    const calculatedWidth = rect.width;

    if (modalElement) {
      // Se está dentro de um modal, usa coordenadas relativas ao viewport
      calculatedLeft = rect.left;
      calculatedTop = rect.bottom;

      // Verifica se o dropdown vai sair da tela horizontalmente
      const viewportWidth = window.innerWidth;
      if (calculatedLeft + calculatedWidth > viewportWidth) {
        calculatedLeft = viewportWidth - calculatedWidth - 10; // 10px de margem
      }

      // Verifica se o dropdown vai sair da tela verticalmente
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 170; // altura máxima do dropdown
      if (calculatedTop + dropdownHeight > viewportHeight) {
        calculatedTop = rect.top - dropdownHeight; // posiciona acima do input
      }
    }

    setDropdownPosition({
      top: calculatedTop,
      left: Math.max(0, calculatedLeft), // garante que não seja negativo
      width: calculatedWidth,
    });
  }, []);

  // Função personalizada para gerenciar cliques fora do componente e scroll
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    function handleScroll(event: Event) {
      const target = event.target as Element;

      // Verifica se o scroll está acontecendo dentro do dropdown
      if (
        dropdownRef.current &&
        (dropdownRef.current.contains(target) || dropdownRef.current === target)
      ) {
        // Se o scroll é dentro do dropdown, não fecha
        return;
      }

      // Se o scroll é fora do dropdown, fecha
      setOpen(false);
    }

    function handleResize() {
      calculateDropdownPosition();
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    // Calcula a posição inicial
    calculateDropdownPosition();

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [open, calculateDropdownPosition]);

  useEffect(() => {
    if (isSelected) {
      setOpen(false);
    }
  }, [isSelected]);

  useEffect(() => {
    setOpen(false);
  }, [value]);

  const handleSearchIconClick = () => {
    calculateDropdownPosition();
    setOpen(true);
    onClickSearchIcon();
  };

  return (
    <Box ref={wrapperRef} width="100%">
      <Flex alignItems="center">
        {isRequirement && (
          <Text color="red.500" mr="1" fontWeight="bold">
            *
          </Text>
        )}
        <Text fontWeight="medium" fontSize="sm" color={textColor}>
          {title}
        </Text>
      </Flex>

      <InputGroup size="md">
        <Input
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onChange={onChangeInput}
          maxLength={maxLength}
          borderWidth={2}
          borderColor={borderColor}
          focusBorderColor={borderColor}
          _focus={{ borderColor: borderColor, borderWidth: "1.5px" }}
          _hover={{ color: "none" }}
          color={textColor}
          height="44px"
          fontSize="13px"
          fontWeight="medium"
          isDisabled={disabled}
        />
        <InputRightElement width={isSelected ? "40px" : "44px"} height="44px">
          {isSelected ? (
            <Box
              cursor={disabled ? "not-allowed" : "pointer"}
              color={textColor}
              onClick={disabled ? undefined : () => {}}
            >
              <AiOutlineCloseCircle
                onClick={disabled ? () => {} : onChangeIcon}
                fontSize="22px"
              />
            </Box>
          ) : (
            <Flex
              alignItems="center"
              justifyContent="center"
              bg={disabledButton ? disabledColor : primaryColor}
              height="100%"
              width="100%"
              borderRightRadius="7px"
              cursor={disabledButton ? "not-allowed" : "pointer"}
              onClick={disabledButton ? undefined : handleSearchIconClick}
            >
              <BiSearch color="white" size={22} />
            </Flex>
          )}
        </InputRightElement>
      </InputGroup>

      {open && (
        <Portal>
          <Box
            ref={dropdownRef}
            position="fixed"
            maxHeight={height || "170px"}
            minHeight="50px"
            bg="white"
            borderRadius="8px"
            mt="1"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
            zIndex={99999}
            overflowY="auto"
            left={`${dropdownPosition.left}px`}
            top={`${dropdownPosition.top}px`}
            width={`${dropdownPosition.width}px`}
            // Previne que o scroll dentro do dropdown seja propagado
            onScroll={(e) => e.stopPropagation()}
          >
            {loading ? (
              <Flex
                justifyContent="center"
                alignItems="center"
                py="4"
                minHeight="50px"
              >
                <Spinner />
              </Flex>
            ) : (
              <List p="2" width="100%">
                {children}
              </List>
            )}
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default InputSelectSearchAccount;
